import { RequestActionTypes } from './createRequestActionTypes'
import { createRequestActions, FetchErrorInfo, NoFetchActionAdditionalData } from './createRequestActions'
import { Dispatch } from 'redux'
import { AppStateWithData } from '../../redux/AppStateWithData'
import { getRequestHeaders } from '../getRequestHeaders/getRequestHeaders'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

export interface RequestThunkParameters<TResult, TBody = never, TActionParameters = NoFetchActionAdditionalData> {
    requestActionTypes: RequestActionTypes
    url: string
    method: HttpMethod
    body?: TBody
    asyncPrepareBody?: Promise<TBody>
    actionAdditionalData?: TActionParameters
    processResultBeforeFetch?: (requestPaylaod: any) => any
    processResultBeforeDispatch?: (requestResult: any) => TResult
    processErrorBeforeDispatch?: (exception: any) => FetchErrorInfo
}

export interface AdditionalOptions  {
    ignoreStart?: boolean
    ignoreSuccess?: boolean
}

export const createRequestThunk = <TResult, TBody, TActionParameters = NoFetchActionAdditionalData>(requestThunkParameters: RequestThunkParameters<TResult, TBody, TActionParameters>, options?: AdditionalOptions ) =>
    async (dispatch: Dispatch, getState: () => AppStateWithData) => {
        const { requestActionTypes, body, actionAdditionalData, method, processResultBeforeDispatch, url,
            processErrorBeforeDispatch, asyncPrepareBody } = requestThunkParameters
        const actions = createRequestActions<TResult, TActionParameters>(requestActionTypes, actionAdditionalData)

        options?.ignoreStart ? dispatch(actions.startWithoutSnackbar())   : dispatch(actions.start())
        
        const token = getState().data.auth.token
        if (!token) {
            dispatch(actions.error("Can't find user's token", null))
            return
        }

        const headers: Headers = getRequestHeaders(token)
        try {
            const fetchParameters: RequestInit = {
                headers,
                method
            }
            if (body) {
                fetchParameters.body = JSON.stringify(body)
                headers.set('Content-Type', 'application/json')
            }
            if (asyncPrepareBody) {
                headers.set('Content-Type', 'application/json')
                fetchParameters.body = JSON.stringify(await asyncPrepareBody)
            }
            const result = await fetch(url, fetchParameters)
            
            if(!result.ok) {
                const e = new Error("An error occurred, please contact Tuqqi support")
                if (processErrorBeforeDispatch) {
                    const error = processErrorBeforeDispatch(e)
                    dispatch(actions.error(error.message, error.errorObject))
                } else {
                    dispatch(actions.error(`Error while fetching from "${url}"`, e))
                }
                return
            }
            const responseBodyText = await result.text()
            const requestResult = responseBodyText.length ? JSON.parse(responseBodyText) : {}
            if (processResultBeforeDispatch) {
                const resultToDispatch = processResultBeforeDispatch(requestResult)
                if(options?.ignoreSuccess) return
                dispatch(actions.success(resultToDispatch))
            } else {
                if(options?.ignoreSuccess) return
                dispatch(actions.success(requestResult as TResult))
            }
        } catch (e) {
            if (processErrorBeforeDispatch) {
                const error = processErrorBeforeDispatch(e)
                dispatch(actions.error(error.message, error.errorObject))
            } else {
                dispatch(actions.error(`Error while fetching from "${url}"`, e))
            }
        }
    }